import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import {
  ADD_ASC_COST_FILE,
  AddAscCostFileMutation,
  AddAscCostFileMutationVariables,
  GET_ASC_COST_SIGNED_UPLOAD_URL,
  GET_BACKGROUND_OPERATIONS,
  GET_COMPANY,
  GET_ONE_SCHEMA_JWT,
  GetAscCostSignedUploadUrlMutation,
  GetAscCostSignedUploadUrlMutationVariables,
  GetBackgroundOperationsQueryVariables,
  GetCompanyQueryVariables,
  GetOneSchemaJwtMutation,
  GetOneSchemaJwtMutationVariables,
  PeriodName,
  useCurrentUser,
} from '@neo.tax/shared';

type UseAscCostUploadParams = {
  companyId: string;
  fiscalYearId: string;
  isOpen: boolean;
  periodName: PeriodName;
};

type UseAscCostUploadReturn = {
  handleSuccess: () => Promise<void>;
  isLoading: boolean;
  oneSchemaJwt: string | null;
  permanentUrl: string | null;
  signedUrl: string | null;
};

export const useAscCostUpload = ({
  companyId,
  fiscalYearId,
  isOpen,
  periodName,
}: UseAscCostUploadParams): UseAscCostUploadReturn => {
  const { user } = useCurrentUser();
  const [getOneSchemaJwt, { data: oneSchemaJwtData }] = useMutation<
    GetOneSchemaJwtMutation,
    GetOneSchemaJwtMutationVariables
  >(GET_ONE_SCHEMA_JWT);
  const [getUploadUrlMutation, { data: uploadUrlData, loading: isLoading }] = useMutation<
    GetAscCostSignedUploadUrlMutation,
    GetAscCostSignedUploadUrlMutationVariables
  >(GET_ASC_COST_SIGNED_UPLOAD_URL);
  const [addAscCostFile] = useMutation<AddAscCostFileMutation, AddAscCostFileMutationVariables>(ADD_ASC_COST_FILE);

  // Fetch OneSchema JWT when modal opens
  useEffect(() => {
    const initOneSchemaJwt = async (): Promise<void> => {
      if (!isOpen || !user?.id) {
        return;
      }
      await getOneSchemaJwt({
        variables: { userId: user.id },
        context: {
          errorToast: {
            title: 'Error uploading ASC cost file',
          },
        },
      });
    };

    initOneSchemaJwt();
  }, [companyId, getOneSchemaJwt, isOpen, user?.id]);

  // Fetch signed upload URL when period is selected
  useEffect(() => {
    const initUploadUrls = async (): Promise<void> => {
      if (!isOpen || !periodName) {
        return;
      }
      const fileName = `asc-cost-${periodName.toLowerCase()}.csv`;
      await getUploadUrlMutation({
        variables: { companyId, fileName },
        context: {
          errorToast: {
            title: 'Error uploading ASC cost file',
          },
        },
      });
    };

    initUploadUrls();
  }, [companyId, getUploadUrlMutation, isOpen, periodName]);

  const handleSuccess = useCallback(async (): Promise<void> => {
    const permanentUrl = uploadUrlData?.getAscCostSignedUploadUrl.permanentUrl;
    if (!permanentUrl) {
      return;
    }
    await addAscCostFile({
      variables: {
        fiscalYearId,
        periodName,
        addAscCostFileInput: {
          permanentUrl,
        },
      },
      refetchQueries: [
        {
          query: GET_COMPANY,
          variables: { companyId } satisfies GetCompanyQueryVariables,
        },
        {
          query: GET_BACKGROUND_OPERATIONS,
          variables: { companyId } satisfies GetBackgroundOperationsQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
      context: {
        successToast: {
          title: 'ASC cost file import started!',
          description:
            "We're processing your cost file. This usually takes a few minutes. When it's done, the data will load automatically on this page.",
        },
      },
    });
  }, [addAscCostFile, companyId, fiscalYearId, uploadUrlData, periodName]);

  return {
    handleSuccess,
    isLoading,
    oneSchemaJwt: oneSchemaJwtData?.getOneSchemaJwt ?? null,
    permanentUrl: uploadUrlData?.getAscCostSignedUploadUrl.permanentUrl ?? null,
    signedUrl: uploadUrlData?.getAscCostSignedUploadUrl.signedUrl ?? null,
  };
};
